/**
 * ChannelRowsPicker - one location name row per channel of a multi-channel
 * device (ConXagent), used by the pour create wizard and the pour edit page.
 *
 * Presentational only: state lives in useChannelBatch so the pairs picked
 * between channels survive closing the dialog. Channels already on the pour
 * show greyed with their existing name. "Too long" and "already on this
 * pour" show as the user types; blank names and clashes within the batch
 * only after a save attempt (showErrors).
 *
 * With showDifferentials, each ticked row also gets a "Differentials" button
 * that opens the same checklist as the table's Differentials column: the
 * other ticked channels of this device, then every eligible location already
 * on the pour (pourDiffOptions). A pair between two channels has no
 * direction, so ticking Bottom under Top also shows Top ticked under Bottom.
 *
 * With functionOptions, each ticked row also gets its own Function switch
 * (Temperature, or Maturity), so one device can carry probes
 * that compute maturity and probes that only record temperature.
 */
import React from 'react';
import { Box, Checkbox, TextField, Typography } from '@mui/material';
import {
  channelDiffPartners,
  channelShortLabel,
  ChannelRow,
  ChannelRowError,
  CHANNEL_ROW_ERROR_TEXT,
  LOCATION_NAME_MAX,
  siblingDiffId,
} from '../../utils/channelRows';
import type { LocationFunctionValue } from '../../utils/locationFunction';
import DifferentialsCell, { DiffPartnerOption } from './DifferentialsCell';
import LocationFunctionToggle from './LocationFunctionToggle';

/** Suggested names, in the order sites usually wire the probes. */
const NAME_PLACEHOLDERS: Record<number, string> = {
  1: 'e.g. Top',
  2: 'e.g. Middle',
  3: 'e.g. Bottom',
  4: 'e.g. Ambient',
};

/** Errors shown while typing; the rest wait for a save attempt. */
const LIVE_ERRORS: ChannelRowError[] = ['tooLong', 'duplicateOnPour'];

const HELPER_SX = { ml: 0, mt: 0.25, fontSize: '0.7rem' };

/** Fits "Differentials (12)". */
const DIFF_BUTTON_WIDTH = 116;

export interface ChannelRowsPickerProps {
  rows: ChannelRow[];
  onChannelNameChange: (no: number, value: string) => void;
  onCheckedChange: (no: number, checked: boolean) => void;
  /** Enter in a name field (Save & add next device). */
  onEnter?: () => void;
  /** False until a hardware number is picked: rows stay locked. */
  deviceSelected: boolean;
  /** Show blank / in-batch duplicate errors (set by a save attempt). */
  showErrors: boolean;
  /** Locks every field, e.g. while saving. */
  disabled?: boolean;
  /** Goes on the first free channel's name field (focused after a device
   *  is picked). */
  firstNameInputRef?: React.Ref<HTMLInputElement>;
  /** Show a "Differentials" button on each ticked row (the dialog decides,
   *  e.g. when the differentials field is visible). */
  showDifferentials?: boolean;
  /** Eligible locations already on the pour, for every row's checklist. */
  pourDiffOptions?: DiffPartnerOption[];
  /** True when two channels are paired, either order (useChannelBatch
   *  fills it in). */
  isPairSelected?: (a: number, b: number) => boolean;
  /** Keys of the pour locations channel `no` is paired with. */
  pourPartnersFor?: (no: number) => string[];
  /** Applies a row's checklist (siblingDiffId for channels, keys for pour
   *  locations). */
  onDiffChange?: (no: number, ids: string[]) => void;
  /** The Functions each ticked row can pick from (concrete hardware). No
   *  Function choice without them. */
  functionOptions?: { value: LocationFunctionValue; label: string }[];
  /** Sets a row's Function (useChannelBatch fills it in). */
  onFunctionChange?: (no: number, fn: LocationFunctionValue) => void;
  /** How the pour calculates maturity ("Temperature-Time Factor"), named
   *  once above the rows when Concrete Maturity is an option. */
  maturityName?: string;
}

const NONE_PAIRED = (): boolean => false;
const NO_PARTNERS = (): string[] => [];
const NO_OPTIONS: DiffPartnerOption[] = [];

const ChannelRowsPicker: React.FC<ChannelRowsPickerProps> = ({
  rows,
  onChannelNameChange,
  onCheckedChange,
  onEnter,
  deviceSelected,
  showErrors,
  disabled = false,
  firstNameInputRef,
  showDifferentials = false,
  pourDiffOptions = NO_OPTIONS,
  isPairSelected = NONE_PAIRED,
  pourPartnersFor = NO_PARTNERS,
  onDiffChange,
  functionOptions,
  onFunctionChange,
  maturityName,
}) => {
  const showFunction = !!functionOptions && functionOptions.length > 0;
  const maturityOffered = !!functionOptions?.some((o) => o.value === 'maturity');
  const diffsLocked = disabled || !deviceSelected || !onDiffChange;
  const firstFreeNo = rows.find((r) => !r.used)?.no;

  // Enter saves and moves on to the next device. Ignored while saving and
  // while an IME is composing (Enter there confirms the composed text).
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    if (disabled || e.nativeEvent.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    onEnter?.();
  };

  const visibleError = (row: ChannelRow): ChannelRowError | null => {
    if (!row.error) return null;
    if (showErrors || LIVE_ERRORS.includes(row.error)) return row.error;
    return null;
  };

  const helperFor = (row: ChannelRow, error: ChannelRowError | null): string | undefined => {
    if (row.used) {
      return row.usedByName ? `Already added as ${row.usedByName}` : 'Already added';
    }
    if (error) return CHANNEL_ROW_ERROR_TEXT[error];
    if (!row.checked) return 'Not added';
    return undefined;
  };

  /** One row's checklist: the other ticked channels, then the pour. */
  const diffCellFor = (row: ChannelRow) => {
    const siblings = channelDiffPartners(rows, row.no).map((r) => ({
      id: siblingDiffId(r.no),
      label: channelShortLabel(r),
      no: r.no,
    }));
    const pourKeys = new Set(pourPartnersFor(row.no));
    const options = [...siblings.map(({ id, label }) => ({ id, label })), ...pourDiffOptions];
    const partners = [
      ...siblings.filter((s) => isPairSelected(row.no, s.no)).map(({ id, label }) => ({ id, label })),
      ...pourDiffOptions.filter((o) => pourKeys.has(o.id)),
    ];
    return (
      <DifferentialsCell
        variant="button"
        buttonLabel={`Differentials for Ch ${row.no}`}
        partners={partners}
        options={options}
        editable
        disabled={diffsLocked}
        onChange={(ids) => onDiffChange?.(row.no, ids)}
      />
    );
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
        Channels
      </Typography>
      {!deviceSelected && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
          Pick a hardware number to see its channels.
        </Typography>
      )}
      {maturityOffered && deviceSelected && maturityName && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Pick what each channel calculates. Maturity is calculated as {maturityName}.
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
        {rows.map((row) => {
          const rowLocked = disabled || !deviceSelected || row.used;
          const error = row.used ? null : visibleError(row);
          const showDiff = showDifferentials && row.checked;
          return (
            <Box
              key={row.no}
              data-testid={`channel-row-${row.no}`}
              sx={{
                display: 'grid',
                // A fixed column for the button, so every name field keeps
                // the same width whatever the button says.
                gridTemplateColumns: showDifferentials
                  ? `auto auto minmax(0, 1fr) ${DIFF_BUTTON_WIDTH}px`
                  : 'auto auto minmax(0, 1fr)',
                columnGap: 0.5,
                alignItems: 'start',
                opacity: row.used ? 0.6 : 1,
              }}
            >
              <Checkbox
                size="small"
                checked={row.checked}
                onChange={(e) => onCheckedChange(row.no, e.target.checked)}
                disabled={rowLocked}
                inputProps={{ 'aria-label': `Add channel ${row.no}` }}
                sx={{ p: 0.75, mt: 0.5 }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  lineHeight: '40px',
                  pr: 0.5,
                  color: row.used || !deviceSelected ? 'text.disabled' : 'text.primary',
                }}
              >
                Ch {row.no}
              </Typography>
              <Box
                sx={{
                  // The Function goes under the name: the dialog is narrow.
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <TextField
                  size="small"
                  value={row.used ? '' : row.name}
                  onChange={(e) => onChannelNameChange(row.no, e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={row.used ? undefined : NAME_PLACEHOLDERS[row.no] || 'e.g. Top'}
                  fullWidth
                  disabled={rowLocked}
                  error={!!error}
                  inputRef={row.no === firstFreeNo ? firstNameInputRef : undefined}
                  inputProps={{ maxLength: LOCATION_NAME_MAX, 'aria-label': `Channel ${row.no} name` }}
                  helperText={helperFor(row, error)}
                  FormHelperTextProps={{ sx: HELPER_SX }}
                />
                {showFunction && row.checked && (
                  <Box>
                    <LocationFunctionToggle
                      value={row.fn}
                      onChange={(fn: LocationFunctionValue) => onFunctionChange?.(row.no, fn)}
                      maturityAllowed={maturityOffered}
                      maturityName={maturityName}
                      disabled={rowLocked || !onFunctionChange}
                      ariaLabel={`Channel ${row.no} function`}
                    />
                  </Box>
                )}
              </Box>
              {showDifferentials && <Box sx={{ minWidth: 0 }}>{showDiff && diffCellFor(row)}</Box>}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ChannelRowsPicker;
